import { __decorate } from 'tslib';
import clsx from 'clsx';
import { unsafeCSS, css, LitElement, html } from 'lit';
import { property, query, customElement } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { getBgColorClass } from '../../types/bgColors.js';
import '../Text/text.js';
import css_248z from './textField.styles.css.js';

/**
 * Text Field Component.
 *
 * @element ht-text-field
 * @csspart text-field The text field.
 */
let HTTextField = class HTTextField extends LitElement {
    constructor() {
        super(...arguments);
        this.bgColor = null;
        this.caption = '';
        this.classLabel = '';
        this.class = '';
        this.error = '';
        this.fieldType = null;
        this.focused = false;
        this.id = '';
        this.label = '';
        this.latestValue = '';
        this.placeholder = '';
        this.size = 'normal';
        this.theme = 'normal';
        this.value = '';
        this.inputRef = createRef();
        this._hasLeft = false;
        this._hasRight = false;
    }
    get wrapperFieldStyle() {
        var _a, _b;
        const inputValue = (_b = (_a = this.inputRef) === null || _a === void 0 ? void 0 : _a.value) === null || _b === void 0 ? void 0 : _b.value;
        return clsx({
            'input-wrapper': true,
            '--has-value': !!this.value || !!inputValue,
            '--with-focus': this.focused,
            '--has-label': !!this.label,
            '--size-small': this.size === 'small',
            '--size-normal': this.size === 'normal' || !this.size,
            '--size-large': this.size === 'large',
            '--size-huge': (!!this.children && !this.size) || this.size === 'huge',
            '--theme-subtle': this.theme === 'subtle',
            '--theme-normal': this.theme === 'normal',
            ...(this.bgColor ? { [getBgColorClass(this.bgColor)]: true } : {}),
        });
    }
    get textFieldStyle() {
        return clsx({
            'input-wrapper__element': !this.fieldType,
            [this.fieldType]: !!this.fieldType,
            [this.class]: !!this.class,
            '--with-left-content': this._hasLeft,
            '--with-right-content': this._hasRight,
        });
    }
    get labelStyle() {
        return clsx({
            'input-wrapper__label': true,
            [this.classLabel]: !!this.classLabel,
        });
    }
    /**
     * Handles `slotchange` event on the `<slot>` for the title icon.
     */
    _handleSlotCaptionChange({ target }) {
        var _a;
        const hasCaption = target.assignedNodes().length > 0;
        (_a = this.captionWrapper) === null || _a === void 0 ? void 0 : _a.toggleAttribute('hidden', !hasCaption);
    }
    _handleSlotChildrenContentChange({ target }) {
        var _a;
        const hasChildrenContent = target.assignedNodes().length > 0;
        (_a = this.childrenContentWrapper) === null || _a === void 0 ? void 0 : _a.toggleAttribute('hidden', !hasChildrenContent);
    }
    _handleSlotLeftChange({ target }) {
        var _a;
        const hasLeft = target.assignedNodes().length > 0;
        this._hasLeft = hasLeft;
        (_a = this.leftWrapper) === null || _a === void 0 ? void 0 : _a.toggleAttribute('hidden', !hasLeft);
    }
    _handleSlotRightChange({ target }) {
        var _a;
        const hasRight = target.assignedNodes().length > 0;
        this._hasRight = hasRight;
        (_a = this.rightWrapper) === null || _a === void 0 ? void 0 : _a.toggleAttribute('hidden', !hasRight);
    }
    handleInput(event) {
        const input = event.target;
        this.value = input.value;
        this.dispatchEvent(new CustomEvent('change'));
    }
    handleFocus() {
        this.focused = true;
        this.dispatchEvent(new CustomEvent('focus'));
    }
    handleBlur() {
        this.focused = false;
        this.dispatchEvent(new CustomEvent('blur'));
    }
    render() {
        var _a;
        return html `
      <div class=${this.wrapperFieldStyle}>
        <div class="input-wrapper__children-content" hidden>
          <slot
            @slotchange=${this._handleSlotChildrenContentChange}
            name="content"
          ></slot>
        </div>
        <div
          class="input-wrapper__cell --left-cell"
          hidden
          aria-hidden="true"
          id="left-wrapper"
        >
          <slot name="left" @slotchange=${this._handleSlotLeftChange}></slot>
        </div>
        <div class="input-wrapper__box">
          <div class="input-wrapper__box-content">
            <div class="input-wrapper__cell">
              <input
                ${ref(this.inputRef)}
                id=${this.id}
                type=${(_a = this.fieldType) !== null && _a !== void 0 ? _a : 'text'}
                class=${this.textFieldStyle}
                .value="${this.value}"
                @input="${this.handleInput}"
                @focus="${this.handleFocus}"
                @blur="${this.handleBlur}"
                placeholder=${this.placeholder}
              />
              ${this.label
            ? html `<label for=${this.id} class=${this.labelStyle}>
                    ${this.label}
                  </label>`
            : null}
            </div>
            <div
              class="input-wrapper__cell --right-cell"
              hidden
              aria-hidden="true"
              id="right-wrapper"
            >
              <slot
                @slotchange=${this._handleSlotRightChange}
                name="right"
              ></slot>
            </div>
          </div>
          <div
            class="input-wrapper__cell input-wrapper__annotation-caption"
            hidden
          >
            <slot name="caption" @slotchange=${this._handleSlotCaptionChange}>
            </slot>
          </div>
        </div>
      </div>
      ${this.error
            ? html ` <div class="input-wrapper__error">
            <ht-text
              preset="caption"
              color="tertiary"
              text=${this.error}
            ></ht-text>
          </div>`
            : null}
    `;
    }
};
HTTextField.styles = css `
    ${unsafeCSS(css_248z)}
  `;
__decorate([
    property({ type: String })
], HTTextField.prototype, "bgColor", void 0);
__decorate([
    property({ type: String })
], HTTextField.prototype, "caption", void 0);
__decorate([
    property({ type: String })
], HTTextField.prototype, "classLabel", void 0);
__decorate([
    property({ type: String })
], HTTextField.prototype, "class", void 0);
__decorate([
    property({ type: String })
], HTTextField.prototype, "error", void 0);
__decorate([
    property({ type: String })
], HTTextField.prototype, "fieldType", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], HTTextField.prototype, "focused", void 0);
__decorate([
    property({ type: String })
], HTTextField.prototype, "id", void 0);
__decorate([
    property({ type: String })
], HTTextField.prototype, "label", void 0);
__decorate([
    property({ type: String })
], HTTextField.prototype, "latestValue", void 0);
__decorate([
    property({ type: String })
], HTTextField.prototype, "placeholder", void 0);
__decorate([
    property({ type: String })
], HTTextField.prototype, "size", void 0);
__decorate([
    property({ type: String })
], HTTextField.prototype, "theme", void 0);
__decorate([
    property({ type: String })
], HTTextField.prototype, "value", void 0);
__decorate([
    query('.input-wrapper__annotation-caption')
], HTTextField.prototype, "captionWrapper", void 0);
__decorate([
    query('.input-wrapper__children-content')
], HTTextField.prototype, "childrenContentWrapper", void 0);
__decorate([
    query('#left-wrapper')
], HTTextField.prototype, "leftWrapper", void 0);
__decorate([
    query('#right-wrapper')
], HTTextField.prototype, "rightWrapper", void 0);
HTTextField = __decorate([
    customElement('ht-text-field')
], HTTextField);
var HTTextField$1 = HTTextField;

export { HTTextField$1 as default };
//# sourceMappingURL=textField.js.map
